import React, { useState, useEffect } from 'react'

export interface CustomDateTime {
  date: string // DD/MM/YYYY format
  time: string // HH:MM format (24-hour)
  period: 'AM' | 'PM'
}

export interface CustomDateTimeRange {
  fromDateTime: CustomDateTime
  toDateTime: CustomDateTime
}

interface CustomDateTimeFilterProps {
  value?: CustomDateTimeRange | null
  onChange: (range: CustomDateTimeRange | null) => void
  onToggleCustomMode?: (enabled: boolean) => void
  isCustomModeEnabled?: boolean
}

// Helper function to convert 12-hour to 24-hour format
const convertTo24Hour = (time12: string, period: 'AM' | 'PM'): string => {
  const [hours, minutes] = time12.split(':').map(num => parseInt(num))
  let hours24 = hours
  
  if (period === 'AM' && hours === 12) {
    hours24 = 0
  } else if (period === 'PM' && hours !== 12) {
    hours24 = hours + 12
  }
  
  return `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}

// Helper function to convert 24-hour to 12-hour format
const convertTo12Hour = (time24: string): { time: string, period: 'AM' | 'PM' } => {
  const [hours, minutes] = time24.split(':').map(num => parseInt(num))
  let period: 'AM' | 'PM' = 'AM'
  let hours12 = hours
  
  if (hours === 0) {
    hours12 = 12
  } else if (hours === 12) {
    period = 'PM'
  } else if (hours > 12) {
    hours12 = hours - 12
    period = 'PM'
  }
  
  return {
    time: `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
    period
  }
}

// Helper function to validate DD/MM/YYYY date format
const isValidDate = (dateStr: string): boolean => {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/
  const match = dateStr.match(regex)
  
  if (!match) return false
  
  const day = parseInt(match[1])
  const month = parseInt(match[2])
  const year = parseInt(match[3])
  
  // Basic validation
  if (month < 1 || month > 12) return false
  if (day < 1 || day > 31) return false
  if (year < 1900 || year > 2100) return false
  
  // Check if date actually exists
  const date = new Date(year, month - 1, day)
  return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year
}

// Helper function to validate HH:MM time format
const isValidTime = (timeStr: string): boolean => {
  const regex = /^(\d{1,2}):(\d{2})$/
  const match = timeStr.match(regex)
  
  if (!match) return false
  
  const hours = parseInt(match[1])
  const minutes = parseInt(match[2])
  
  return hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59
}

// Helper function to get current date/time in the required format
const getCurrentDateTime = (): CustomDateTime => {
  const now = new Date()
  const date = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`
  const time24 = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
  const { time, period } = convertTo12Hour(time24)
  
  return { date, time, period }
}

export const CustomDateTimeFilter: React.FC<CustomDateTimeFilterProps> = ({
  value,
  onChange,
  onToggleCustomMode,
  isCustomModeEnabled = false
}) => {
  const [isEnabled, setIsEnabled] = useState(isCustomModeEnabled)
  const [fromDate, setFromDate] = useState('')
  const [fromTime, setFromTime] = useState('')
  const [fromPeriod, setFromPeriod] = useState<'AM' | 'PM'>('AM')
  const [toDate, setToDate] = useState('')
  const [toTime, setToTime] = useState('')
  const [toPeriod, setToPeriod] = useState<'AM' | 'PM'>('PM')
  
  const [errors, setErrors] = useState<{
    fromDate?: string
    fromTime?: string
    toDate?: string
    toTime?: string
    range?: string
  }>({})

  // Initialize with current values or defaults
  useEffect(() => {
    if (value) {
      setFromDate(value.fromDateTime.date)
      setFromTime(value.fromDateTime.time)
      setFromPeriod(value.fromDateTime.period)
      setToDate(value.toDateTime.date)
      setToTime(value.toDateTime.time)
      setToPeriod(value.toDateTime.period)
      setIsEnabled(true)
    } else if (isEnabled) {
      // Set default values when enabling
      const now = getCurrentDateTime()
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      const yesterdayStr = `${yesterday.getDate().toString().padStart(2, '0')}/${(yesterday.getMonth() + 1).toString().padStart(2, '0')}/${yesterday.getFullYear()}`
      
      setFromDate(yesterdayStr)
      setFromTime('12:00')
      setFromPeriod('AM')
      setToDate(now.date)
      setToTime(now.time)
      setToPeriod(now.period)
    }
  }, [value, isEnabled])

  const validateAndUpdate = () => {
    const newErrors: typeof errors = {}
    
    // Validate dates
    if (!isValidDate(fromDate)) {
      newErrors.fromDate = 'Invalid date format (DD/MM/YYYY)'
    }
    if (!isValidDate(toDate)) {
      newErrors.toDate = 'Invalid date format (DD/MM/YYYY)'
    }
    
    // Validate times
    if (!isValidTime(fromTime)) {
      newErrors.fromTime = 'Invalid time format (HH:MM, 1-12)'
    }
    if (!isValidTime(toTime)) {
      newErrors.toTime = 'Invalid time format (HH:MM, 1-12)'
    }
    
    // Validate range if both dates and times are valid
    if (!newErrors.fromDate && !newErrors.toDate && !newErrors.fromTime && !newErrors.toTime) {
      const fromDateTime = new Date()
      const toDateTime = new Date()
      
      // Parse from date
      const [fromDay, fromMonth, fromYear] = fromDate.split('/').map(num => parseInt(num))
      const fromTime24 = convertTo24Hour(fromTime, fromPeriod)
      const [fromHours, fromMinutes] = fromTime24.split(':').map(num => parseInt(num))
      fromDateTime.setFullYear(fromYear, fromMonth - 1, fromDay)
      fromDateTime.setHours(fromHours, fromMinutes, 0, 0)
      
      // Parse to date
      const [toDay, toMonth, toYear] = toDate.split('/').map(num => parseInt(num))
      const toTime24 = convertTo24Hour(toTime, toPeriod)
      const [toHours, toMinutes] = toTime24.split(':').map(num => parseInt(num))
      toDateTime.setFullYear(toYear, toMonth - 1, toDay)
      toDateTime.setHours(toHours, toMinutes, 0, 0)
      
      if (fromDateTime >= toDateTime) {
        newErrors.range = 'From date/time must be before to date/time'
      }
      
      // Check if dates are not in the future
      const now = new Date()
      if (fromDateTime > now) {
        newErrors.fromDate = 'From date cannot be in the future'
      }
      if (toDateTime > now) {
        newErrors.toDate = 'To date cannot be in the future'
      }
    }
    
    setErrors(newErrors)
    
    // If no errors, update the parent component
    if (Object.keys(newErrors).length === 0 && isEnabled) {
      const newRange: CustomDateTimeRange = {
        fromDateTime: { date: fromDate, time: fromTime, period: fromPeriod },
        toDateTime: { date: toDate, time: toTime, period: toPeriod }
      }
      onChange(newRange)
    } else if (!isEnabled) {
      onChange(null)
    }
  }

  // Update parent when values change
  useEffect(() => {
    if (isEnabled) {
      const timer = setTimeout(validateAndUpdate, 300) // Debounce validation
      return () => clearTimeout(timer)
    }
  }, [fromDate, fromTime, fromPeriod, toDate, toTime, toPeriod, isEnabled])

  const handleToggle = (enabled: boolean) => {
    setIsEnabled(enabled)
    onToggleCustomMode?.(enabled)
    
    if (!enabled) {
      setErrors({})
      onChange(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* Toggle Switch */}
      <div className="flex items-center space-x-3">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => handleToggle(e.target.checked)}
            className="sr-only"
          />
          <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
            isEnabled ? 'bg-blue-600' : 'bg-gray-300'
          }`}>
            <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 ${
              isEnabled ? 'translate-x-5' : 'translate-x-0'
            }`} />
          </div>
          <span className="ml-3 text-sm font-medium text-gray-700">
            Use Custom Date & Time Range
          </span>
        </label>
      </div>

      {/* Warning Message */}
      {isEnabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-amber-400 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-amber-800">
              Custom date/time filtering will override the preset time range filter. Only one filtering method can be active at a time.
            </p>
          </div>
        </div>
      )}

      {/* Custom Date/Time Inputs */}
      {isEnabled && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h4 className="text-sm font-semibold text-gray-900">Custom Date & Time Range</h4>
          
          {/* From Date/Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <input
                  type="text"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="DD/MM/YYYY"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.fromDate 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.fromDate && (
                  <p className="mt-1 text-xs text-red-600">{errors.fromDate}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  placeholder="HH:MM"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.fromTime 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.fromTime && (
                  <p className="mt-1 text-xs text-red-600">{errors.fromTime}</p>
                )}
              </div>
              <div>
                <select
                  value={fromPeriod}
                  onChange={(e) => setFromPeriod(e.target.value as 'AM' | 'PM')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* To Date/Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <input
                  type="text"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="DD/MM/YYYY"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.toDate 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.toDate && (
                  <p className="mt-1 text-xs text-red-600">{errors.toDate}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  placeholder="HH:MM"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    errors.toTime 
                      ? 'border-red-300 focus:ring-red-500' 
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                />
                {errors.toTime && (
                  <p className="mt-1 text-xs text-red-600">{errors.toTime}</p>
                )}
              </div>
              <div>
                <select
                  value={toPeriod}
                  onChange={(e) => setToPeriod(e.target.value as 'AM' | 'PM')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Range Error */}
          {errors.range && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-800">{errors.range}</p>
            </div>
          )}
          
          {/* Format Help */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <h5 className="text-sm font-medium text-blue-900 mb-1">Format Guide:</h5>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Date: DD/MM/YYYY (e.g., 25/12/2023)</li>
              <li>• Time: HH:MM in 12-hour format (e.g., 02:30)</li>
              <li>• Select AM or PM from the dropdown</li>
              <li>• From date/time must be before to date/time</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
